import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Drawer from '@mui/material/Drawer';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/esm/Add';
import RefreshIcon from '@mui/icons-material/esm/Refresh';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import readFirstRegisterService from '../../services/api/readFirstRegisterService';

type HseType =
  | 'incident'
  | 'accident'
  | 'jsa'
  | 'hazard'
  | 'ppe'
  | 'safety-inspection'
  | 'emergency-contact'
  | 'stand-down'
  | 'first-aid';

export const hseTypes: Array<{
  key: HseType;
  label: string;
  endpoint: string;
  statusKey?: string;
  canCreate?: boolean;
}> = [
  { key: 'incident', label: 'Incident', endpoint: '/incidents', statusKey: 'status', canCreate: true },
  { key: 'accident', label: 'Accident', endpoint: '/accidents' },
  { key: 'jsa', label: 'JSA', endpoint: '/jsas', statusKey: 'approvalStatus' },
  { key: 'hazard', label: 'Hazard', endpoint: '/hazard-assessments', statusKey: 'riskLevel' },
  { key: 'ppe', label: 'PPE Check', endpoint: '/ppe-checks' },
  { key: 'safety-inspection', label: 'Safety Inspection', endpoint: '/safety-inspections', statusKey: 'result', canCreate: true },
  { key: 'emergency-contact', label: 'Emergency Contact', endpoint: '/emergency-contacts' },
  { key: 'stand-down', label: 'Stand-down', endpoint: '/safety-stand-downs', statusKey: 'status' },
  { key: 'first-aid', label: 'First-aid Readiness', endpoint: '/first-aid-readiness', statusKey: 'status' },
];

const firstValue = (row: any, keys: string): any => {
  for (const key of keys.split('|')) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return '-';
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(value))) return new Date(value).toLocaleDateString();
  return String(value);
};

const today = () => new Date().toISOString().slice(0, 10);

export default function HseRegisterPage() {
  const activeProjectId = useSelector((state: RootState) => state.projects.activeProjectId);
  const [type, setType] = useState<HseType>('incident');
  const [rows, setRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [incidentForm, setIncidentForm] = useState({
    incidentNumber: '',
    severity: 'low',
    description: '',
    occurredAt: '',
  });
  const [inspectionForm, setInspectionForm] = useState({
    inspectionNumber: '',
    inspectionType: 'general',
    assetRef: '',
    location: '',
    inspectionDate: today(),
    result: 'pass',
  });

  const activeType = useMemo(() => hseTypes.find((item) => item.key === type) ?? hseTypes[0], [type]);

  const load = async () => {
    if (!activeProjectId) {
      setRows([]);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setRows(await readFirstRegisterService.list(activeType.endpoint, { projectId: activeProjectId }));
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to load ${activeType.label}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId, activeType.endpoint]);

  const createRecord = async () => {
    if (!activeProjectId) return;
    try {
      setError(null);
      setNotice(null);
      if (type === 'incident') {
        await readFirstRegisterService.create('/incidents', {
          projectId: activeProjectId,
          incidentNumber: incidentForm.incidentNumber.trim(),
          severity: incidentForm.severity,
          description: incidentForm.description.trim(),
          occurredAt: incidentForm.occurredAt || null,
        });
        setIncidentForm({ incidentNumber: '', severity: 'low', description: '', occurredAt: '' });
      } else if (type === 'safety-inspection') {
        await readFirstRegisterService.create('/safety-inspections', {
          projectId: activeProjectId,
          inspectionNumber: inspectionForm.inspectionNumber.trim(),
          inspectionType: inspectionForm.inspectionType,
          assetRef: inspectionForm.assetRef.trim(),
          location: inspectionForm.location.trim() || null,
          inspectionDate: inspectionForm.inspectionDate || null,
          result: inspectionForm.result,
        });
        setInspectionForm({
          inspectionNumber: '',
          inspectionType: 'general',
          assetRef: '',
          location: '',
          inspectionDate: today(),
          result: 'pass',
        });
      }
      setDialogOpen(false);
      setNotice(`${activeType.label} created.`);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to create ${activeType.label}`);
    }
  };

  const canSubmit = type === 'incident'
    ? incidentForm.incidentNumber.trim() && incidentForm.description.trim()
    : inspectionForm.inspectionNumber.trim() && inspectionForm.assetRef.trim();

  return (
    <Box>
      <PageHeader
        title="HSE Register"
        subtitle="One desktop register for HSE review, filtering, and confirmed write paths"
        actions={
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>Refresh</Button>
            {activeType.canCreate && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                New {activeType.label}
              </Button>
            )}
          </Stack>
        }
      />

      {!activeProjectId && <Alert severity="info" sx={{ mb: 2 }}>Select a project to load HSE records.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {!activeType.canCreate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Create/edit UI is not wired for {activeType.label.toLowerCase()} in this wave; the register remains visible for review.
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Record type</InputLabel>
          <Select value={type} label="Record type" onChange={(event) => setType(event.target.value as HseType)}>
            {hseTypes.map((item) => (
              <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Chip label={`${rows.length} row${rows.length === 1 ? '' : 's'}`} />
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : (
        <Paper sx={{ borderRadius: 1, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Summary</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="right">Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const status = firstValue(row, 'severity|riskLevel|result|approvalStatus|status');
                return (
                  <TableRow key={row.id || index} hover>
                    <TableCell>{formatValue(firstValue(row, 'incidentNumber|accidentNumber|jsaNumber|inspectionNumber|standDownNumber|name|contactName|id'))}</TableCell>
                    <TableCell>{formatValue(firstValue(row, 'title|subject|assetRef|description|reason|role|phone|mobile|email'))}</TableCell>
                    <TableCell>{status === '-' ? '-' : <Chip size="small" label={formatValue(status)} />}</TableCell>
                    <TableCell>{formatValue(firstValue(row, 'taskId|teamId|location|projectId'))}</TableCell>
                    <TableCell>{formatValue(firstValue(row, 'reportedAt|accidentDate|checkDate|inspectionDate|startedAt|createdAt'))}</TableCell>
                    <TableCell align="right"><Button size="small" onClick={() => setSelected(row)}>Open</Button></TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info">No {activeType.label.toLowerCase()} rows returned.</Alert>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New {activeType.label}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {type === 'incident' ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Incident Number" value={incidentForm.incidentNumber} onChange={(event) => setIncidentForm({ ...incidentForm, incidentNumber: event.target.value })} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Severity" value={incidentForm.severity} onChange={(event) => setIncidentForm({ ...incidentForm, severity: event.target.value })}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="critical">Critical</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}><TextField fullWidth type="datetime-local" label="Occurred At" InputLabelProps={{ shrink: true }} value={incidentForm.occurredAt} onChange={(event) => setIncidentForm({ ...incidentForm, occurredAt: event.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth multiline minRows={3} label="Description" value={incidentForm.description} onChange={(event) => setIncidentForm({ ...incidentForm, description: event.target.value })} /></Grid>
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Inspection Number" value={inspectionForm.inspectionNumber} onChange={(event) => setInspectionForm({ ...inspectionForm, inspectionNumber: event.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Asset Ref" value={inspectionForm.assetRef} onChange={(event) => setInspectionForm({ ...inspectionForm, assetRef: event.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth label="Location" value={inspectionForm.location} onChange={(event) => setInspectionForm({ ...inspectionForm, location: event.target.value })} /></Grid>
              <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Inspection Date" InputLabelProps={{ shrink: true }} value={inspectionForm.inspectionDate} onChange={(event) => setInspectionForm({ ...inspectionForm, inspectionDate: event.target.value })} /></Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Result" value={inspectionForm.result} onChange={(event) => setInspectionForm({ ...inspectionForm, result: event.target.value })}>
                  <MenuItem value="pass">Pass</MenuItem>
                  <MenuItem value="fail">Fail</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Type" value={inspectionForm.inspectionType} onChange={(event) => setInspectionForm({ ...inspectionForm, inspectionType: event.target.value })}>
                  <MenuItem value="general">General</MenuItem>
                  <MenuItem value="scaffold">Scaffold</MenuItem>
                  <MenuItem value="excavation">Excavation</MenuItem>
                  <MenuItem value="lifting">Lifting</MenuItem>
                  <MenuItem value="access">Access</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createRecord} disabled={!canSubmit}>Create</Button>
        </DialogActions>
      </Dialog>

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        <Box sx={{ width: { xs: 360, sm: 520 }, p: 3 }}>
          <Typography variant="h6">{activeType.label} Detail</Typography>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {selected && Object.entries(selected).map(([key, value]) => (
              <Box key={key}>
                <Typography variant="caption" color="text.secondary">{key}</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{formatValue(value)}</Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
}
